import { useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { Probe, ProbeGroup, Schedule } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  Spinner,
  StatusBadge,
} from '../components/common'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

// Ricorrenze pronte all'uso: evitano di dover conoscere la sintassi cron.
// Le etichette sono funzioni: `t()` si chiama quando si mostrano, non
// all'import del modulo.
const PRESETS: { id: string; label: () => string; interval?: number; cron?: string }[] = [
  { id: 'every_15m', label: () => t('Ogni 15 minuti'), interval: 15 },
  { id: 'hourly', label: () => t('Ogni ora'), interval: 60 },
  { id: 'every_6h', label: () => t('Ogni 6 ore'), interval: 360 },
  { id: 'daily_2am', label: () => t('Ogni giorno alle 02:00'), cron: '0 2 * * *' },
  { id: 'weekly_sun', label: () => t('Ogni domenica alle 03:00'), cron: '0 3 * * 0' },
  { id: 'custom', label: () => t('Espressione cron personalizzata') },
]

export default function Schedules() {
  const schedules = useAsync(() => api.schedules(), [])
  const credentials = useAsync(() => api.credentials(), [])
  const credentialGroups = useAsync(() => api.credentialGroups(), [])
  const kinds = useAsync(() => api.discoveryKinds(), [])

  const [name, setName] = useState('')
  // Era fissato a ``snmp_sweep``: si poteva pianificare solo una scansione di
  // rete, mentre l'inventario cloud e quello di virtualizzazione — che sono
  // proprio quelli che si vogliono ricorrenti, perché cambiano da soli —
  // andavano riavviati a mano ogni volta.
  const [kind, setKind] = useState('snmp_sweep')
  const [targets, setTargets] = useState('')
  const [credentialId, setCredentialId] = useState('')
  const [preset, setPreset] = useState('daily_2am')
  const [cron, setCron] = useState('0 2 * * *')
  // Il fuso del browser è quasi sempre quello giusto: proporlo evita che
  // «alle 2:00» significhi in silenzio le 2:00 UTC.
  const [tz, setTz] = useState(browserTimezone())
  const [jitter, setJitter] = useState('0')
  const [windowStart, setWindowStart] = useState('')
  const [windowEnd, setWindowEnd] = useState('')
  const [misfire, setMisfire] = useState<'skip' | 'catch_up'>('skip')
  const [overlap, setOverlap] = useState<'skip' | 'queue'>('skip')
  const [maxRetries, setMaxRetries] = useState('0')
  const [backoff, setBackoff] = useState('10')
  const [disableAfter, setDisableAfter] = useState('0')
  const [deep, setDeep] = useState(true)
  const [pingFirst, setPingFirst] = useState(true)
  const [reverseDns, setReverseDns] = useState(false)
  const [recordUnmanaged, setRecordUnmanaged] = useState(false)
  const [topology, setTopology] = useState(false)
  const [entityInventory, setEntityInventory] = useState(false)
  const [routingTable, setRoutingTable] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Quale pianificazione si sta modificando. `null` = se ne sta creando una.
  //
  // Lo stesso modulo per le due cose, e non è per risparmiare codice: due
  // moduli separati divergono al primo campo aggiunto, e chi modifica si
  // troverebbe davanti meno opzioni di chi crea — senza che niente lo dica.
  const [modifica, setModifica] = useState<Schedule | null>(null)
  const [macTable, setMacTable] = useState(false)
  const [poolIds, setPoolIds] = useState<string[]>([])
  const pools = useAsync(() => api.pools(), [])
  const probes = useAsync(() => api.probes(), [])
  const groups = useAsync(() => api.groups(), [])
  // Where the jobs run: «probe:<id>», «group:<id>» or empty = any eligible
  // probe. One menu, as in the job form: probe and group are alternatives.
  const [runOn, setRunOn] = useState('')
  const [selezionati, setSelezionati] = useState<Set<string>>(new Set())

  const selected = PRESETS.find((p) => p.id === preset)
  const selectedKind = kinds.data?.find((k) => k.kind === kind)
  // Solo le credenziali del tipo giusto: una community SNMP su una
  // pianificazione AWS verrebbe rifiutata a ogni esecuzione, per sempre, da un
  // log della sonda invece che da questo modulo.
  const usableCredentials =
    credentials.data?.filter((c) => selectedKind?.credential_kinds.includes(c.kind) ?? true) ?? []
  // Un gruppo è proponibile se **almeno una** delle sue credenziali è del tipo
  // giusto: la sonda le prova in sequenza e salta quelle che non si applicano,
  // quindi un gruppo misto resta utile.
  const usableGroups =
    credentialGroups.data?.filter((g) =>
      g.members.some((m) => m.kind && (selectedKind?.credential_kinds.includes(m.kind) ?? true)),
    ) ?? []

  // Only the probes that can run this kind, and the groups with at least one.
  const sondeIdonee = (probes.data ?? []).filter((p) => !selectedKind || p.effective_capabilities.includes(selectedKind.capability))
  const gruppiConSonda = new Set(sondeIdonee.map((p) => p.group_id).filter((id): id is string => id != null))
  const gruppiIdonei = (groups.data ?? []).filter((g) => gruppiConSonda.has(g.id))

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const comune = {
        name,
        kind,
        targets: selectedKind?.needs_targets
          ? targets.split(',').map((t) => t.trim()).filter(Boolean)
          : [],
        // Un solo menu nella console, due campi nell'API: «group:<id>» è un
        // gruppo ordinato, tutto il resto una credenziale singola. Sono
        // alternative: comporle richiederebbe una regola su quale viene prima.
        credential_id: credentialId.startsWith('group:') ? null : credentialId || null,
        credential_group_id: credentialId.startsWith('group:')
          ? credentialId.slice('group:'.length)
          : null,
        // L'espressione del preset scelto; quella scritta a mano solo con
        // «personalizzata». Prima partiva sempre il campo a mano, che cambia
        // solo quando lo si vede: scegliere «Ogni domenica alle 03:00»
        // salvava la ricorrenza precedente e la colonna Frequenza non si
        // muoveva mai.
        cron_expression: selected?.interval ? null : (selected?.cron ?? cron),
        interval_minutes: selected?.interval ?? null,
        timezone: tz,
        jitter_seconds: Number(jitter) || 0,
        window_start_hour: windowStart ? Number(windowStart) : null,
        window_end_hour: windowEnd ? Number(windowEnd) : null,
        // Il recupero ha senso solo su cron: un intervallo non ha occorrenze
        // «previste» da recuperare, e il Control Plane lo rifiuterebbe.
        misfire_policy: selected?.interval ? 'skip' : misfire,
        overlap_policy: overlap,
        max_retries: Number(maxRetries) || 0,
        retry_backoff_minutes: Number(backoff) || 10,
        disable_after_failures: Number(disableAfter) || 0,
        deep,
        ping_first: pingFirst,
        reverse_dns: reverseDns,
        record_unmanaged: recordUnmanaged,
        topology,
        mac_table: macTable,
        entity_inventory: entityInventory,
        routing_table: routingTable,
        pool_ids: poolIds,
        probe_id: runOn.startsWith('probe:') ? runOn.slice(6) : null,
        group_id: runOn.startsWith('group:') ? runOn.slice(6) : null,
      }
      if (modifica) {
        // In modifica il tipo non si tocca: cambiarlo vorrebbe dire un'altra
        // pianificazione con lo stesso storico, e lo storico direbbe cose su
        // esecuzioni che non sono mai state di questo tipo.
        const { kind: _tipo, ...modificabili } = comune
        await api.updateSchedule(modifica.id, modificabili)
        setNotice(t('Pianificazione «{nome}» aggiornata.', { nome: name }))
      } else {
        await api.createSchedule(comune)
      }
      annullaModifica()
      schedules.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  /** Carica una pianificazione nel modulo, per modificarla. */
  function apriModifica(s: Schedule) {
    setModifica(s)
    setName(s.name)
    setKind(s.kind)
    const spec = (s.spec ?? {}) as Record<string, unknown>
    setTargets(((spec.targets as string[]) ?? []).join(', '))
    setPoolIds(((spec.pool_ids as string[]) ?? []).map(String))
    // Le credenziali **non** si ripropongono, ed è deliberato: la spec conserva
    // l'elenco già espanso, quindi non si sa più se venivano da un gruppo o da
    // una credenziale singola. Riproporne una indovinata la farebbe confermare
    // senza rileggerla, cambiando quali si provano; lasciando il campo vuoto
    // restano quelle di prima, che è ciò che vuole chi sta cambiando l'ambito.
    setCredentialId('')
    setRunOn(s.probe_id ? `probe:${s.probe_id}` : s.group_id ? `group:${s.group_id}` : '')
    setCron(s.cron_expression ?? '0 2 * * *')
    setPreset(
      s.interval_minutes
        ? (PRESETS.find((p) => p.interval === s.interval_minutes)?.id ?? 'daily_2am')
        : (PRESETS.find((p) => p.cron === s.cron_expression)?.id ?? 'custom'),
    )
    setTz(s.timezone || browserTimezone())
    setJitter(String(s.jitter_seconds ?? 0))
    setWindowStart(s.window_start_hour != null ? String(s.window_start_hour) : '')
    setWindowEnd(s.window_end_hour != null ? String(s.window_end_hour) : '')
    setMisfire((s.misfire_policy as 'skip' | 'catch_up') ?? 'skip')
    setOverlap((s.overlap_policy as 'skip' | 'queue') ?? 'skip')
    setMaxRetries(String(s.max_retries ?? 0))
    setBackoff(String(s.retry_backoff_minutes ?? 10))
    setDisableAfter(String(s.disable_after_failures ?? 0))
    setDeep(spec.deep !== false)
    setPingFirst(Boolean(spec.ping_first))
    setReverseDns(Boolean(spec.reverse_dns))
    setRecordUnmanaged(Boolean(spec.record_unmanaged))
    setTopology(Boolean(spec.topology))
    setMacTable(Boolean(spec.mac_table))
    setEntityInventory(Boolean(spec.entity_inventory))
    setRoutingTable(Boolean(spec.routing_table))
    setError(null)
    setNotice(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function annullaModifica() {
    setModifica(null)
    setName('')
    setTargets('')
    setPoolIds([])
    setCredentialId('')
    setRunOn('')
  }

  async function toggle(s: Schedule) {
    setError(null)
    try {
      await api.updateSchedule(s.id, { enabled: !s.enabled })
      schedules.reload()
    } catch (err) {
      setError(err)
    }
  }

  async function runNow(s: Schedule) {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      const job = await api.runScheduleNow(s.id)
      setNotice(
        t('«{nome}» avviata ora (job {job}…). La prossima esecuzione programmata resta invariata.', {
          nome: s.name,
          job: job.id.slice(0, 8),
        }),
      )
      schedules.reload()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('Eliminare questa pianificazione? I job non partiranno più da soli. Non si può annullare.'))) return
    setError(null)
    try {
      await api.deleteSchedule(id)
      schedules.reload()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Scheduler')}</h1>
          <p className="page__subtitle">{t('Pianifica l’esecuzione ricorrente dei job di discovery, definendo frequenza e periodicità, per mantenere automaticamente aggiornati i Configuration Item.')}</p>
        </div>
      </header>

      <ErrorBanner error={error ?? schedules.error} />
      {notice && <div className="banner banner--ok">{notice}</div>}

      <Card
        title={
          modifica ? t('Modifica «{nome}»', { nome: modifica.name }) : t('Nuova pianificazione')
        }
      >
        {/* Chi modifica deve vedere subito che sta modificando, e cosa non
            cambia: il tipo è fissato, e le credenziali restano quelle se non le
            si tocca — perché la spec ne conserva l'elenco già espanso e
            riproporne una indovinata la farebbe confermare senza rileggerla. */}
        {modifica && (
          <div className="banner banner--info">
            {t("Stai modificando una pianificazione che gira già. Il tipo non si cambia; le credenziali restano quelle attuali se non ne scegli un'altra. Lo storico delle esecuzioni resta.")}
          </div>
        )}
        {credentials.data?.length === 0 ? (
          <EmptyState
            title={t('Nessuna credenziale configurata')}
            hint={t('Crea prima una credenziale nella sezione Credenziali.')}
          />
        ) : (
          <form onSubmit={onSubmit}>
            <div className="row">
              <div className="field">
                <label htmlFor="name">{t('Nome')}</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('Scansione notturna sede')}
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="sched-kind">{t('Tipo di scansione')}</label>
                <select
                  id="sched-kind"
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value)
                    // Una credenziale non più pertinente resterebbe selezionata
                    // in silenzio, e il salvataggio fallirebbe senza che sia
                    // chiaro il perché.
                    setCredentialId('')
                  }}
                >
                  {kinds.data?.map((k) => (
                    <option key={k.kind} value={k.kind} disabled={!k.licensed}>
                      {k.licensed
                        ? `${t(k.family_label)} · ${t(k.label)}`
                        : t('{sistema} — non compreso nella licenza', {
                            sistema: `${t(k.family_label)} · ${t(k.label)}`,
                          })}
                    </option>
                  ))}
                </select>
                {selectedKind?.hint && <p className="field__hint">{t(selectedKind.hint)}</p>}
              </div>
              {selectedKind?.needs_targets && (
                <div className="field" style={{ flex: '2 1 260px' }}>
                  <label htmlFor="targets">{t('Ambito')}</label>
                  <input
                    id="targets"
                    type="text"
                    value={targets}
                    onChange={(e) => setTargets(e.target.value)}
                    placeholder="192.168.1.0/24"
                    required={poolIds.length === 0}
                  />
                  <p className="field__hint">
                    {poolIds.length > 0
                      ? t('Si aggiunge ai pool scelti.')
                      : t('Subnet, indirizzi o intervalli, separati da virgola.')}
                  </p>
                </div>
              )}
              {/* I pool anche qui, e non solo nel modulo dei job: una
                  pianificazione ne conserva il **riferimento**, quindi una
                  subnet aggiunta al pool entra nella scansione della notte
                  successiva. È il caso in cui servono di più, ed era l'unico
                  posto in cui non si potevano scegliere. */}
              {selectedKind?.needs_targets && (pools.data ?? []).length > 0 && (
                <div className="field" style={{ flex: '1 1 220px' }}>
                  <label htmlFor="sched-pools">{t('Pool di asset')}</label>
                  <select
                    id="sched-pools"
                    multiple
                    size={Math.min(Math.max((pools.data ?? []).length, 2), 4)}
                    value={poolIds}
                    onChange={(e) =>
                      setPoolIds(Array.from(e.target.selectedOptions, (o) => o.value))
                    }
                  >
                    {(pools.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {t('{nome} · {n} ambiti', { nome: p.name, n: p.entries_count })}
                      </option>
                    ))}
                  </select>
                  <p className="field__hint">
                    {t('Per riferimento: una subnet aggiunta al pool entra nella scansione successiva.')}
                  </p>
                </div>
              )}
              <div className="field">
                <label htmlFor="cred">{t('Credenziale')}</label>
                <select
                  id="cred"
                  value={credentialId}
                  onChange={(e) => setCredentialId(e.target.value)}
                  // In modifica non è obbligatoria: lasciandola vuota restano
                  // quelle attuali, che è ciò che vuole chi sta cambiando
                  // l'ambito.
                  required={!modifica}
                >
                  <option value="">
                    {modifica ? t('Lascia quelle attuali') : t('Seleziona…')}
                  </option>
                  {/* I gruppi per primi: quando ne esiste uno è quasi sempre la
                      scelta giusta — è stato creato apposta perché una
                      credenziale sola non bastava. */}
                  {usableGroups.length > 0 && (
                    <optgroup label={t('Gruppi (provati in ordine)')}>
                      {usableGroups.map((g) => (
                        <option key={g.id} value={`group:${g.id}`}>
                          {t('{nome} — {n} credenziali', { nome: g.name, n: g.members.length })}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label={t('Credenziale singola')}>
                    {usableCredentials.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.kind})
                      </option>
                    ))}
                  </optgroup>
                </select>
                {credentialId.startsWith('group:') && (
                  <p className="field__hint">
                    {t('La sonda le prova in ordine e si ferma alla prima che risponde. Su una pianificazione il costo si ripete a ogni esecuzione: una credenziale sbagliata in cima aggiunge un timeout per ogni apparato, ogni notte.')}
                  </p>
                )}
              </div>
              <div className="field">
                <label htmlFor="sched-runon">{t('Esegui su')}</label>
                <select id="sched-runon" value={runOn} onChange={(e) => setRunOn(e.target.value)}>
                  <option value="">{t('Qualunque sonda idonea')}</option>
                  <OpzioniEsecutore sonde={sondeIdonee} gruppi={gruppiIdonei} attuale={runOn} />
                </select>
                <p className="field__hint">
                  {runOn.startsWith('probe:')
                    ? t('Ogni esecuzione la fa solo questa sonda; se non è disponibile il job aspetta. È anche la sonda che poi legge l’apparato nel monitoraggio, perché è quella che le sue ACL lasciano passare.')
                    : runOn.startsWith('group:')
                      ? t('Ogni esecuzione la fa una sonda di questo gruppo.')
                      : t('La prende la prima sonda libera che sa farla: con più sedi può essere quella di un’altra rete. Scegli la sonda o il gruppo della sede.')}
                </p>
              </div>
            </div>

            <div className="row">
              <div className="field">
                <label htmlFor="preset">{t('Frequenza')}</label>
                <select id="preset" value={preset} onChange={(e) => setPreset(e.target.value)}>
                  {PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label()}
                    </option>
                  ))}
                </select>
              </div>

              {preset === 'custom' && (
                <div className="field">
                  <label htmlFor="cron">{t('Espressione cron')}</label>
                  <input
                    id="cron"
                    type="text"
                    value={cron}
                    onChange={(e) => setCron(e.target.value)}
                    placeholder="0 2 * * *"
                    className="mono"
                    required
                  />
                  <p className="field__hint">{t('minuto ora giorno mese giorno-settimana')}</p>
                </div>
              )}

              <div className="field">
                <label htmlFor="tz">{t('Fuso orario')}</label>
                <select id="tz" value={tz} onChange={(e) => setTz(e.target.value)}>
                  {timezoneOptions(tz).map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
                <p className="field__hint">
                  {t("L'orario è interpretato in questo fuso: «alle 02:00» sono le 02:00 qui, anche quando cambia l'ora legale.")}
                </p>
              </div>

              <div className="field" style={{ flex: '0 1 130px' }}>
                <label htmlFor="ws">{t('Finestra da (ora)')}</label>
                <input
                  id="ws"
                  type="number"
                  min={0}
                  max={23}
                  value={windowStart}
                  onChange={(e) => setWindowStart(e.target.value)}
                  placeholder="22"
                />
              </div>
              <div className="field" style={{ flex: '0 1 130px' }}>
                <label htmlFor="we">{t('a (ora)')}</label>
                <input
                  id="we"
                  type="number"
                  min={0}
                  max={23}
                  value={windowEnd}
                  onChange={(e) => setWindowEnd(e.target.value)}
                  placeholder="6"
                />
              </div>
            </div>
            <p className="field__hint" style={{ marginTop: -6, marginBottom: 14 }}>
              {t("La finestra limita l'orario in cui le scansioni possono partire: utile per evitare gli orari di lavoro. Lasciala vuota per non porre limiti.")}
            </p>

            <div className="row">
              <div className="field" style={{ flex: '0 1 150px' }}>
                <label htmlFor="jitter">{t('Ritardo casuale (s)')}</label>
                <input
                  id="jitter"
                  type="number"
                  min={0}
                  max={3600}
                  value={jitter}
                  onChange={(e) => setJitter(e.target.value)}
                />
                <p className="field__hint">
                  {t("Distribuisce l'avvio quando più pianificazioni hanno lo stesso orario.")}
                </p>
              </div>

              {!selected?.interval && (
                <div className="field">
                  <label htmlFor="misfire">{t('Esecuzioni perse')}</label>
                  <select
                    id="misfire"
                    value={misfire}
                    onChange={(e) => setMisfire(e.target.value as 'skip' | 'catch_up')}
                  >
                    <option value="skip">{t('Salta e riparti dalla prossima')}</option>
                    <option value="catch_up">{t('Recupera quelle saltate')}</option>
                  </select>
                  <p className="field__hint">
                    {t("Che fare se il sistema era fermo all'orario previsto. Di un inventario interessa quasi sempre lo stato attuale.")}
                  </p>
                </div>
              )}

              <div className="field">
                <label htmlFor="overlap">{t('Se la precedente è ancora in corso')}</label>
                <select
                  id="overlap"
                  value={overlap}
                  onChange={(e) => setOverlap(e.target.value as 'skip' | 'queue')}
                >
                  <option value="skip">{t('Salta questo turno')}</option>
                  <option value="queue">{t('Avvia comunque')}</option>
                </select>
              </div>
            </div>

            <div className="row">
              <div className="field" style={{ flex: '0 1 150px' }}>
                <label htmlFor="retries">{t('Ritentativi')}</label>
                <input
                  id="retries"
                  type="number"
                  min={0}
                  max={5}
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: '0 1 180px' }}>
                <label htmlFor="backoff">{t('Attesa fra i tentativi (min)')}</label>
                <input
                  id="backoff"
                  type="number"
                  min={1}
                  max={1440}
                  value={backoff}
                  onChange={(e) => setBackoff(e.target.value)}
                  disabled={Number(maxRetries) === 0}
                />
                <p className="field__hint">{t('Raddoppia a ogni tentativo successivo.')}</p>
              </div>
              <div className="field" style={{ flex: '0 1 220px' }}>
                <label htmlFor="disable">{t('Sospendi dopo N fallimenti')}</label>
                <input
                  id="disable"
                  type="number"
                  min={0}
                  max={100}
                  value={disableAfter}
                  onChange={(e) => setDisableAfter(e.target.value)}
                />
                <p className="field__hint">{t('0 = mai.')}</p>
              </div>
            </div>

            {/* Le opzioni sono quelle della scansione di rete: mostrarle su un
                inventario cloud proporrebbe scelte che il connettore ignora. */}
            <div style={{ display: selectedKind?.options.length ? undefined : 'none' }}>
            <div className="checkbox">
              <input id="deep" type="checkbox" checked={deep} onChange={(e) => setDeep(e.target.checked)} />
              <label htmlFor="deep">{t('Discovery approfondita')}</label>
            </div>
            <div className="checkbox">
              <input
                id="ping"
                type="checkbox"
                checked={pingFirst || recordUnmanaged}
                disabled={recordUnmanaged}
                onChange={(e) => setPingFirst(e.target.checked)}
              />
              <label htmlFor="ping">{t('Verifica raggiungibilità prima di interrogare')}</label>
            </div>
            <div className="checkbox">
              <input
                id="dns"
                type="checkbox"
                checked={reverseDns}
                onChange={(e) => setReverseDns(e.target.checked)}
              />
              <label htmlFor="dns">{t('Risoluzione DNS inversa')}</label>
            </div>
            <div className="checkbox">
              <input
                id="topo"
                type="checkbox"
                checked={topology}
                onChange={(e) => setTopology(e.target.checked)}
              />
              <label htmlFor="topo">{t('Rileva i collegamenti tra apparati (CDP/LLDP)')}</label>
            </div>
            {/* La tabella MAC si manda già al Control Plane, ma qui non si
                poteva spuntare: l'opzione esisteva e non era raggiungibile —
                una funzione visibile a metà, che è peggio di una che non c'è. */}
            <div className="checkbox">
              <input
                id="mactab"
                type="checkbox"
                checked={macTable}
                onChange={(e) => setMacTable(e.target.checked)}
              />
              <label htmlFor="mactab">
                {t('Rileva cosa è attaccato a ogni porta')}
                <small>
                  {t('Legge gli indirizzi fisici imparati dalle porte dello switch e li correla con i CI già in inventario: vede anche ciò che non parla CDP/LLDP.')}
                </small>
              </label>
            </div>
            <div className="checkbox">
              <input
                id="unm"
                type="checkbox"
                checked={recordUnmanaged}
                onChange={(e) => setRecordUnmanaged(e.target.checked)}
              />
              <label htmlFor="unm">{t('Registra anche i dispositivi senza SNMP')}</label>
            </div>
            {selectedKind?.options.includes('entity_inventory') && (
              <div className="checkbox">
                <input
                  id="entity"
                  type="checkbox"
                  checked={entityInventory}
                  onChange={(e) => setEntityInventory(e.target.checked)}
                />
                <label htmlFor="entity">
                  {t('Inventario componenti (SFP, schede, alimentatori)')}
                  <small>
                    {t('Legge dalla ENTITY-MIB i componenti fisici con il loro seriale. Richiede la discovery approfondita. Le SFP di terze parti spesso non hanno seriale in SNMP: per quelle è più completo l’inventario avanzato via SSH.')}
                  </small>
                </label>
              </div>
            )}
            {selectedKind?.options.includes('routing_table') && (
              <div className="checkbox">
                <input
                  id="routing"
                  type="checkbox"
                  checked={routingTable}
                  onChange={(e) => setRoutingTable(e.target.checked)}
                />
                <label htmlFor="routing">
                  {t('Tabelle di routing (L3)')}
                  <small>
                    {t('Raccoglie la tabella di routing dell’apparato via SNMP (inetCidrRoute/ipCidrRoute/ipRoute), mostrata nella sezione «Routing» del CI. Richiede la discovery approfondita. Su un router con molte rotte è voluminosa: è troncata a un tetto.')}
                  </small>
                </label>
              </div>
            )}
            </div>

            {selectedKind && !selectedKind.needs_targets && (
              <p className="field__hint" style={{ marginBottom: 12 }}>
                {t("L'inventario copre l'intero account o piattaforma: l'endpoint e l'ambito provengono dalla credenziale.")}
              </p>
            )}

            <div className="rowactions" style={{ textAlign: 'left' }}>
              <button type="submit" disabled={busy}>
                {busy
                  ? t('Salvataggio…')
                  : modifica
                    ? t('Salva le modifiche')
                    : t('Crea pianificazione')}
              </button>
              {modifica && (
                <button type="button" className="secondary" onClick={annullaModifica}>
                  {t('Annulla')}
                </button>
              )}
            </div>
          </form>
        )}
      </Card>

      <Card title={t('Pianificazioni attive')}>
        {schedules.loading && !schedules.data ? (
          <Spinner />
        ) : !schedules.data?.length ? (
          <EmptyState
            title={t('Nessuna pianificazione')}
            hint={t('Le discovery vanno avviate a mano finché non ne crei una.')}
          />
        ) : (
          // Lo stesso modulo di filtro delle altre tabelle: con molte
          // pianificazioni si cerca «quelle sospese», «quelle fallite» o «le
          // scansioni della sede X», e si scrive nell'intestazione.
          <>
          {selezionati.size > 0 && (
            <ModificaMassiva
              ids={[...selezionati]}
              sonde={probes.data ?? []}
              gruppi={groups.data ?? []}
              onFatto={(messaggio) => { setNotice(messaggio); schedules.reload() }}
              onError={setError}
              onAnnulla={() => setSelezionati(new Set())}
            />
          )}
          <DataTable
            rows={schedules.data}
            rowKey={(s) => s.id}
            initialSort={{ key: 'name' }}
            selezione={{ chiavi: selezionati, onCambia: setSelezionati, id: (s) => s.id }}
            columns={[
              {
                key: 'name',
                header: t('Nome'),
                value: (s) => s.name,
                render: (s) => (
                  <>
                    {s.name}
                    {s.paused_reason && <div className="field__hint">{s.paused_reason}</div>}
                  </>
                ),
              },
              {
                // Senza il tipo, una pianificazione AWS e una scansione di rete
                // si leggono identiche: hanno entrambe un nome e una frequenza,
                // e l'ambito della prima è vuoto.
                key: 'kind',
                header: t('Tipo'),
                value: (s) => {
                  const etichetta = kinds.data?.find((k) => k.kind === s.kind)?.label
                  return etichetta ? t(etichetta) : s.kind
                },
              },
              {
                key: 'targets',
                header: t('Ambito'),
                mono: true,
                value: (s) =>
                  Array.isArray(s.spec.targets) && (s.spec.targets as string[]).length
                    ? (s.spec.targets as string[]).join(', ')
                    : '',
              },
              {
                key: 'run_on',
                header: t('Esegui su'),
                value: (s) => s.probe_id
                  ? (probes.data?.find((p) => p.id === s.probe_id)?.name ?? t('sonda non più presente'))
                  : s.group_id
                    ? t('gruppo {nome}', { nome: groups.data?.find((g) => g.id === s.group_id)?.name ?? '?' })
                    : t('qualunque sonda idonea'),
              },
              { key: 'recurrence', header: t('Frequenza'), mono: true, value: (s) => describeRecurrence(s) },
              {
                key: 'enabled',
                header: t('Stato'),
                value: (s) => (s.enabled ? t('attiva') : t('sospesa')),
                render: (s) => <StatusBadge status={s.enabled ? 'attiva' : 'sospesa'} />,
              },
              {
                key: 'outcome',
                header: t('Ultimo esito'),
                value: (s) => (s.last_status ? outcomeLabel(s.last_status) : t('mai eseguita')),
                render: (s) => <Outcome schedule={s} />,
              },
              {
                key: 'last_run_at',
                header: t('Ultima'),
                value: (s) => s.last_run_at ?? '',
                render: (s) => formatDateTime(s.last_run_at),
              },
              {
                key: 'next_run_at',
                header: t('Prossima'),
                value: (s) => (s.enabled ? s.next_run_at ?? '' : ''),
                render: (s) => (s.enabled ? formatDateTime(s.next_run_at) : '—'),
              },
              {
                key: 'azioni',
                header: '',
                filter: false,
                sort: false,
                render: (s) => (
                  // «Modifica» prima di «Elimina», e con più risalto: finora
                  // l'unico modo di cambiare l'ambito di una pianificazione era
                  // eliminarla e rifarla — perdendo lo storico, cioè «da quando
                  // questa scansione va male».
                  <div className="rowactions">
                    <button onClick={() => apriModifica(s)} disabled={busy}>
                      {t('Modifica')}
                    </button>
                    <button className="secondary" onClick={() => runNow(s)} disabled={busy}>
                      {t('Esegui ora')}
                    </button>
                    <button className="secondary" onClick={() => toggle(s)}>
                      {s.enabled ? t('Sospendi') : t('Riattiva')}
                    </button>
                    <button className="danger" onClick={() => remove(s.id)}>
                      {t('Elimina')}
                    </button>
                  </div>
                ),
              },
            ]}
          />
          </>
        )}
      </Card>
    </>
  )
}

/** The options of «Esegui su»: groups first, then probes (marked when offline). */
function OpzioniEsecutore({ sonde, gruppi, attuale }: { sonde: Probe[]; gruppi: ProbeGroup[]; attuale: string }) {
  // A probe or group already set but not offered any more stays visible: a
  // silent «any» would move the schedule to another network.
  const mancante = (attuale.startsWith('probe:') && !sonde.some((p) => `probe:${p.id}` === attuale))
    || (attuale.startsWith('group:') && !gruppi.some((g) => `group:${g.id}` === attuale))
  return (
    <>
      {gruppi.length > 0 && (
        <optgroup label={t('Gruppi di sonde')}>
          {gruppi.map((g) => (
            <option key={g.id} value={`group:${g.id}`}>
              {g.members === 1 ? t('{nome} (1 sonda)', { nome: g.name }) : t('{nome} ({n} sonde)', { nome: g.name, n: g.members })}
            </option>
          ))}
        </optgroup>
      )}
      {sonde.length > 0 && (
        <optgroup label={t('Sonde')}>
          {sonde.map((p) => (
            <option key={p.id} value={`probe:${p.id}`}>
              {p.status === 'online' ? p.name : t('{nome} — non in linea', { nome: p.name })}
            </option>
          ))}
        </optgroup>
      )}
      {mancante && <option value={attuale}>{t('scelta attuale (non idonea per questo tipo)')}</option>}
    </>
  )
}

/**
 * The same change on the selected schedules: where they run — a probe or a
 * group of probes, or any eligible one — and whether they are on. A probe
 * that cannot run a schedule's kind leaves that one as it was, and says so.
 */
function ModificaMassiva({
  ids, sonde, gruppi, onFatto, onError, onAnnulla,
}: {
  ids: string[]
  sonde: Probe[]
  gruppi: ProbeGroup[]
  onFatto: (messaggio: string) => void
  onError: (e: unknown) => void
  onAnnulla: () => void
}) {
  const [esegui, setEsegui] = useState('')
  const [stato, setStato] = useState('')
  const [lavoro, setLavoro] = useState(false)
  async function applica() {
    setLavoro(true)
    onError(null)
    try {
      const r = await api.bulkSchedules({
        ids,
        ...(esegui === 'any' ? { run_on: 'any' as const }
          : esegui.startsWith('probe:') ? { run_on: 'probe' as const, probe_id: esegui.slice(6) }
            : esegui.startsWith('group:') ? { run_on: 'group' as const, group_id: esegui.slice(6) } : {}),
        ...(stato ? { enabled: stato === 'on' } : {}),
      })
      const saltate = r.skipped.map((x) => x.name).join(', ')
      onFatto(r.skipped.length
        ? t('{n} pianificazioni aggiornate; lasciate com’erano perché la sonda non sa eseguirle: {nomi}.', { n: r.updated, nomi: saltate })
        : t('{n} pianificazioni aggiornate.', { n: r.updated }))
      setEsegui('')
      setStato('')
    } catch (e) {
      onError(e)
    } finally {
      setLavoro(false)
    }
  }
  return (
    <div className="pianificazioni-massiva">
      <strong>{t('{n} pianificazioni selezionate', { n: ids.length })}</strong>
      <label className="field">
        <span className="field__label">{t('Esegui su')}</span>
        <select value={esegui} onChange={(e) => setEsegui(e.target.value)}>
          <option value="">{t('lascia com’è')}</option>
          <option value="any">{t('Qualunque sonda idonea')}</option>
          <OpzioniEsecutore sonde={sonde} gruppi={gruppi} attuale="" />
        </select>
      </label>
      <label className="field">
        <span className="field__label">{t('Stato')}</span>
        <select value={stato} onChange={(e) => setStato(e.target.value)}>
          <option value="">{t('lascia com’è')}</option>
          <option value="on">{t('attiva')}</option>
          <option value="off">{t('sospesa')}</option>
        </select>
      </label>
      <button type="button" onClick={() => void applica()} disabled={lavoro || (!esegui && !stato)}>
        {lavoro ? t('Applico…') : t('Applica alle selezionate')}
      </button>
      <button type="button" className="link" onClick={onAnnulla}>{t('Annulla la selezione')}</button>
    </div>
  )
}

function describeRecurrence(s: Schedule): string {
  // Il fuso fa parte della ricorrenza: «0 2 * * *» da solo non dice a che ora
  // parte davvero.
  if (s.cron_expression) {
    return s.timezone && s.timezone !== 'UTC'
      ? `${s.cron_expression} (${s.timezone})`
      : s.cron_expression
  }
  if (!s.interval_minutes) return '—'
  const m = s.interval_minutes
  if (m % 1440 === 0) return t('ogni {n} g', { n: m / 1440 })
  if (m % 60 === 0) return t('ogni {n} h', { n: m / 60 })
  return t('ogni {n} min', { n: m })
}

/** L'etichetta dell'esito, con `t()` letterali chiamate al momento di mostrarla. */
function outcomeLabel(status: string): string {
  switch (status) {
    case 'succeeded': return t('riuscita')
    case 'failed': return t('fallita')
    case 'partial': return t('parziale')
    case 'running': return t('in corso')
    case 'retrying': return t('in ritentativo')
    default: return status
  }
}

const OUTCOME_BADGE: Record<string, string> = {
  succeeded: 'ok',
  failed: 'error',
  partial: 'warn',
  running: 'info',
  retrying: 'warn',
}

/**
 * Esito dell'ultima esecuzione.
 *
 * È la colonna che mancava: prima si vedeva solo *quando* una pianificazione
 * era partita, non *com'era andata*. Una che falliva da un mese appariva
 * identica a una che funziona.
 */
function Outcome({ schedule }: { schedule: Schedule }) {
  if (!schedule.last_status) return <span className="text-muted">{t('mai eseguita')}</span>
  return (
    <>
      <span className={`badge badge--${OUTCOME_BADGE[schedule.last_status] ?? 'muted'}`}>
        {outcomeLabel(schedule.last_status)}
      </span>
      {schedule.consecutive_failures > 1 && (
        <span className="chip">{t('{n} di fila', { n: schedule.consecutive_failures })}</span>
      )}
      {schedule.last_error && <div className="field__hint">{schedule.last_error}</div>}
    </>
  )
}

/** Fuso del browser, quando disponibile. */
function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * Fusi proposti nel modulo.
 *
 * L'elenco completo IANA è di centinaia di voci e non aiuta nessuno: qui ci
 * sono UTC, quello del browser e i principali. Il valore corrente è sempre
 * incluso, così una pianificazione creata altrove non perde il proprio fuso
 * per il solo fatto di essere aperta in modifica.
 */
function timezoneOptions(current: string): string[] {
  const common = [
    'UTC',
    'Europe/Rome',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Madrid',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ]
  return Array.from(new Set([current, browserTimezone(), ...common])).filter(Boolean)
}
